import Image from 'next/image';
import exclamationPointImage from 'public/images/exclamation-point.svg';
import React from 'react';

import { ConditionalHeader } from '@/components/ConditionalHeader/ConditionalHeader';
import { Footer } from '@/components/Footer/Footer';
import { Button } from '@/components/UI/Button';
import footerLinks from '@/utils/footerLinks';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
}

export function ErrorFallback({ error }: ErrorFallbackProps): React.ReactNode {
  return (
    <div data-testid="ErrorFallback" className="flex min-h-screen flex-col bg-base-black bg-fixed">
      <ConditionalHeader />
      <main className="flex min-h-screen grow">
        <div className="m-auto mt-0 flex max-w-[548px] flex-1 flex-col items-center gap-[25px] px-[25px] pb-[200px] pt-[50px] md:max-w-[710px] md:pt-[100px]">
          <Image
            priority
            src={exclamationPointImage}
            alt="Sorry, there was a problem"
            width={156}
            className="md:w-[230px]"
          />
          <h1 className="text-center font-FoxOneAeroMedium text-[30px]/[36px] text-white">
            Sorry, there was a problem
          </h1>
          <p className="mb-[32px] text-center font-FoxOneAero text-[17px]/[22px] text-white md:mb-0">
            We seem to be having trouble loading this page. But don&#39;t worry, you can return home at your
            convenience.
          </p>
          <Button
            size="lg"
            variant="roundWhite"
            className="mx-auto w-full max-w-[320px] font-FoxOneAeroMedium text-[17px]/[22px] text-black"
            href="/"
            dataAnalytics="not-found-home-button"
          >
            Go back home
          </Button>
          {process.env.NODE_ENV === 'development' ? (
            <div className="flex flex-col gap-[25px] rounded-lg bg-navyAccent/20 p-[25px]">
              <p className="text-center font-FoxOneAeroLight text-[16px]/[30px] text-white">
                This is a debugging error message, and won&#39;t appear in production:
              </p>
              <p className="text-white">Error: {error.message}</p>
              <p className="text-white">{error.stack}</p>
            </div>
          ) : null}
        </div>
      </main>
      <Footer links={footerLinks} />
    </div>
  );
}
